import { Component, OnInit, inject } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent
} from "@angular/material/dialog";
import { LocationStore } from "@app/services/store/location-store";
import { RouteService } from "@app/services/http/route.service";
import { PostCodeMessage } from "@app/models/dto/postcode-message";
import { PostCodeSearchMessage } from "@app/models/dto/postcode-search-message";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Observable, Subject, of, from } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  map
} from "rxjs/operators";
import { CdkScrollable } from "@angular/cdk/scrolling";
import {
  MatFormField,
  MatLabel,
  MatSuffix
} from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import {
  MatAutocompleteTrigger,
  MatAutocomplete
} from "@angular/material/autocomplete";

import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatOption } from "@angular/material/core";
import { MatButton } from "@angular/material/button";
import { TranslatePipe } from "@ngx-translate/core";
import { DialogHeaderComponent } from "../dialog-header/dialog-header.component";

@Component({
  selector: "app-location-chooser",
  templateUrl: "./location-chooser.component.html",
  styleUrls: ["./location-chooser.component.css"],
  imports: [
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatProgressSpinner,
    MatSuffix,
    MatAutocomplete,
    MatOption,
    MatButton,
    TranslatePipe,
    DialogHeaderComponent
  ]
})
export class LocationChooserComponent implements OnInit {
  protected routeService = inject(RouteService);
  protected locationStore = inject(LocationStore);
  protected dialog = inject(MatDialog);
  dialogRef = inject<MatDialogRef<LocationChooserComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);

  foundResult: Boolean = false;
  searching: Boolean = false;
  result: PostCodeMessage;

  // Autocomplete properties
  postcodeSearchControl = new FormControl("");
  filteredPostcodes: PostCodeSearchMessage[] = [];
  private searchSubject = new Subject<string>();
  isLoading = false;
  minSearchLength = 3;

  ngOnInit() {
    // Set up the search subscription
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          if (query.length < this.minSearchLength) {
            return of({ results: [] });
          }

          this.isLoading = true;
          return from(this.routeService.searchPostcodes(query)).pipe(
            catchError(() => {
              this.isLoading = false;
              return of({ results: [] });
            })
          );
        })
      )
      .subscribe((response) => {
        this.isLoading = false;
        this.filteredPostcodes = response.results || [];
      });

    // Subscribe to value changes in the search control
    this.postcodeSearchControl.valueChanges.subscribe((value) => {
      if (value && typeof value === "string") {
        this.searchSubject.next(value);
      }
    });
  }

  selectPostcode(postcode: PostCodeSearchMessage) {
    if (!postcode) return;

    this.searching = true;
    // Create a PostCodeMessage from the PostCodeSearchMessage
    const postcodeMessage: PostCodeMessage = {
      postCode: postcode.postcode,
      longitude: postcode.longitude,
      latitude: postcode.latitude
    };

    this.result = postcodeMessage;
    this.foundResult = true;
    this.searching = false;
    this.postcodeSearchControl.setValue("");
    this.filteredPostcodes = [];
  }

  reset() {
    this.searching = false;
    this.result = null;
    this.foundResult = false;
    this.postcodeSearchControl.setValue("");
    this.filteredPostcodes = [];
  }

  confirm() {
    this.dialogRef.close({ result: this.result });
  }

  onCancel() {
    this.dialogRef.close();
  }

  displayPostcode(postcode: PostCodeSearchMessage): string {
    return postcode ? postcode.postcode : "";
  }
}
